/**
 * ⌘K search tokens — plan/35 locked direction.
 * Chip = lens; Open list is the default Enter target after lock.
 */

export type PartySearchToken = {
  type: "party";
  role: "customer" | "supplier";
  id: string;
  name: string;
};

export type ProductSearchToken = {
  type: "product";
  id: string;
  name: string;
  sku?: string | null;
};

/** Object list scope — SO / PO / POS (SI when invoices ship) — plan/35 #3 */
export type ObjectScopeKind = "salesOrders" | "purchaseOrders" | "posTickets";

export type ObjectSearchToken = {
  type: "object";
  object: ObjectScopeKind;
};

/** Work-queue scope — plan/35 #4 */
export type QueueScopeKind =
  | "toCollect"
  | "toDeliver"
  | "toPay"
  | "toReceive";

export type QueueSearchToken = {
  type: "queue";
  queue: QueueScopeKind;
};

export type SearchToken =
  | PartySearchToken
  | ProductSearchToken
  | ObjectSearchToken
  | QueueSearchToken;

export type CommandAction =
  | { kind: "navigate" }
  /** Lock a party chip; do not close the menu */
  | { kind: "lockParty"; token: PartySearchToken }
  /** Lock a product chip; do not close the menu */
  | { kind: "lockProduct"; token: ProductSearchToken }
  /** Lock an object-list scope; do not close the menu */
  | { kind: "lockObject"; token: ObjectSearchToken }
  /** Lock a work-queue scope; do not close the menu */
  | { kind: "lockQueue"; token: QueueSearchToken };

const OBJECT_META: Record<
  ObjectScopeKind,
  { label: string; listLabel: string; keywords: string[]; pathSuffix: string }
> = {
  salesOrders: {
    label: "Sales orders",
    listLabel: "Open sales orders",
    keywords: ["so", "sales", "orders", "sales orders", "sales order"],
    pathSuffix: "/sales/orders",
  },
  purchaseOrders: {
    label: "Purchase orders",
    listLabel: "Open purchase orders",
    keywords: ["po", "purchase", "purchasing", "purchase orders", "purchase order"],
    pathSuffix: "/purchasing/orders",
  },
  posTickets: {
    label: "POS tickets",
    listLabel: "Open POS tickets",
    keywords: ["pos", "tickets", "pos tickets", "register"],
    pathSuffix: "/pos/tickets",
  },
};

export function objectScopeMeta(kind: ObjectScopeKind) {
  return OBJECT_META[kind];
}

export function objectListPath(
  companySlug: string,
  kind: ObjectScopeKind,
): string {
  return `/c/${companySlug}${OBJECT_META[kind].pathSuffix}`;
}

/** Match typed query to object-scope lock candidates — plan/35 #3. */
export function objectScopeLockCandidates(
  companySlug: string,
  query: string,
): Array<{
  id: string;
  label: string;
  hint: string;
  path: string;
  token: ObjectSearchToken;
}> {
  const q = query.trim().toLowerCase();
  if (q.length < 1) return [];
  const out: Array<{
    id: string;
    label: string;
    hint: string;
    path: string;
    token: ObjectSearchToken;
  }> = [];
  for (const kind of Object.keys(OBJECT_META) as ObjectScopeKind[]) {
    const meta = OBJECT_META[kind];
    if (!keywordHits(q, meta.keywords)) continue;
    out.push({
      id: `lock-object-${kind}`,
      label: meta.label,
      hint: "Lock · Scope",
      path: objectListPath(companySlug, kind),
      token: { type: "object", object: kind },
    });
  }
  return out;
}

/**
 * Pre-lock object scope when ⌘K opens from a module list — plan/35 #3.
 * Only exact list roots (not detail /new).
 */
export function objectScopeFromPathname(
  pathname: string,
  companySlug: string,
): ObjectSearchToken | null {
  const root = `/c/${companySlug}`;
  for (const kind of Object.keys(OBJECT_META) as ObjectScopeKind[]) {
    const list = `${root}${OBJECT_META[kind].pathSuffix}`;
    if (pathname === list || pathname === `${list}/`) {
      return { type: "object", object: kind };
    }
  }
  return null;
}

const QUEUE_META: Record<
  QueueScopeKind,
  { label: string; listLabel: string; keywords: string[]; pathSuffix: string }
> = {
  toCollect: {
    label: "To collect",
    listLabel: "Open to collect",
    keywords: [
      "to collect",
      "to-collect",
      "collect",
      "collectibles",
      "receivable",
      "receivables",
    ],
    pathSuffix: "/sales/to-collect",
  },
  toDeliver: {
    label: "To deliver",
    listLabel: "Open to deliver",
    keywords: ["to deliver", "to-deliver", "deliver", "delivery"],
    pathSuffix: "/sales/to-deliver",
  },
  toPay: {
    label: "To pay",
    listLabel: "Open to pay",
    keywords: ["to pay", "to-pay", "pay", "payables", "payable"],
    pathSuffix: "/purchasing/to-pay",
  },
  toReceive: {
    label: "To receive",
    listLabel: "Open to receive",
    keywords: ["to receive", "to-receive", "receive", "receiving"],
    pathSuffix: "/purchasing/to-receive",
  },
};

export function queueListPath(
  companySlug: string,
  kind: QueueScopeKind,
): string {
  return `/c/${companySlug}${QUEUE_META[kind].pathSuffix}`;
}

function keywordHits(q: string, keywords: string[]): boolean {
  // Short queries: exact keyword only ("po" ≠ "pos"). Longer: prefix either way.
  if (q.length <= 2) return keywords.includes(q);
  return keywords.some((k) => k.startsWith(q) || q.startsWith(k));
}

/** Match typed query to queue-scope lock candidates — plan/35 #4. */
export function queueScopeLockCandidates(
  companySlug: string,
  query: string,
): Array<{
  id: string;
  label: string;
  hint: string;
  path: string;
  token: QueueSearchToken;
}> {
  const q = query.trim().toLowerCase();
  if (q.length < 1) return [];
  const out: Array<{
    id: string;
    label: string;
    hint: string;
    path: string;
    token: QueueSearchToken;
  }> = [];
  for (const kind of Object.keys(QUEUE_META) as QueueScopeKind[]) {
    const meta = QUEUE_META[kind];
    if (!keywordHits(q, meta.keywords)) continue;
    out.push({
      id: `lock-queue-${kind}`,
      label: meta.label,
      hint: "Lock · Queue",
      path: queueListPath(companySlug, kind),
      token: { type: "queue", queue: kind },
    });
  }
  return out;
}

export function queueScopeFromPathname(
  pathname: string,
  companySlug: string,
): QueueSearchToken | null {
  const root = `/c/${companySlug}`;
  for (const kind of Object.keys(QUEUE_META) as QueueScopeKind[]) {
    const list = `${root}${QUEUE_META[kind].pathSuffix}`;
    if (pathname === list || pathname === `${list}/`) {
      return { type: "queue", queue: kind };
    }
  }
  return null;
}

/** Pre-lock object or queue when ⌘K opens from that list — plan/35. */
export function scopeTokenFromPathname(
  pathname: string,
  companySlug: string,
): SearchToken | null {
  return (
    objectScopeFromPathname(pathname, companySlug) ??
    queueScopeFromPathname(pathname, companySlug)
  );
}

export function partySalesOrdersListPath(
  companySlug: string,
  party: { id: string; name: string },
): string {
  const params = new URLSearchParams();
  params.set("party", party.id);
  params.set("partyName", party.name);
  return `/c/${companySlug}/sales/orders?${params.toString()}`;
}

export function partyPurchasingOrdersListPath(
  companySlug: string,
  party: { id: string; name: string },
): string {
  const params = new URLSearchParams();
  params.set("party", party.id);
  params.set("partyName", party.name);
  return `/c/${companySlug}/purchasing/orders?${params.toString()}`;
}

export function productDetailPath(
  companySlug: string,
  productId: string,
): string {
  return `/c/${companySlug}/products/${productId}`;
}

/** Default Open-list row after a token lock — plan/35 Enter choreography. */
export function openListItemForToken(
  companySlug: string,
  token: SearchToken,
): {
  id: string;
  label: string;
  hint: string;
  path: string;
} {
  if (token.type === "queue") {
    const meta = QUEUE_META[token.queue];
    return {
      id: `open-list-queue-${token.queue}`,
      label: meta.listLabel,
      hint: "View all",
      path: queueListPath(companySlug, token.queue),
    };
  }
  if (token.type === "object") {
    const meta = OBJECT_META[token.object];
    return {
      id: `open-list-object-${token.object}`,
      label: meta.listLabel,
      hint: "View all",
      path: objectListPath(companySlug, token.object),
    };
  }
  if (token.type === "product") {
    return {
      id: `open-list-product-${token.id}`,
      label: "Open product",
      hint: "View all",
      path: productDetailPath(companySlug, token.id),
    };
  }
  if (token.role === "supplier") {
    return {
      id: `open-list-party-${token.id}`,
      label: "Open purchase orders",
      hint: "View all",
      path: partyPurchasingOrdersListPath(companySlug, token),
    };
  }
  return {
    id: `open-list-party-${token.id}`,
    label: "Open sales orders",
    hint: "View all",
    path: partySalesOrdersListPath(companySlug, token),
  };
}

export function tokenChipLabel(token: SearchToken): string {
  if (token.type === "queue") return QUEUE_META[token.queue].label;
  if (token.type === "object") return OBJECT_META[token.object].label;
  if (token.type === "product") {
    const sku = token.sku?.trim();
    return sku ? `Product · ${token.name} (${sku})` : `Product · ${token.name}`;
  }
  if (token.role === "supplier") return `Supplier · ${token.name}`;
  return `Customer · ${token.name}`;
}

/** Backspace on empty query removes the last chip — plan/35. */
export function shouldClearTokenOnBackspace(
  query: string,
  key: string,
): boolean {
  return key === "Backspace" && query.length === 0;
}

/** v1 stack cap — party + queue|object — plan/35 #5 */
export const MAX_SEARCH_TOKENS = 2;

function queueMatchesPartyRole(
  queue: QueueScopeKind,
  role: "customer" | "supplier",
): boolean {
  if (role === "customer") return queue === "toCollect" || queue === "toDeliver";
  return queue === "toPay" || queue === "toReceive";
}

function objectMatchesPartyRole(
  object: ObjectScopeKind,
  role: "customer" | "supplier",
): boolean {
  if (role === "customer") return object === "salesOrders";
  return object === "purchaseOrders";
}

export function isCompatibleTokenStack(
  tokens: readonly SearchToken[],
): boolean {
  if (tokens.length === 0) return true;
  if (tokens.length > MAX_SEARCH_TOKENS) return false;
  if (tokens.length === 1) return true;
  const party = tokens.find((t) => t.type === "party");
  const queue = tokens.find((t) => t.type === "queue");
  const object = tokens.find((t) => t.type === "object");
  const product = tokens.find((t) => t.type === "product");
  if (product) return false;
  if (party && queue && !object) {
    return queueMatchesPartyRole(queue.queue, party.role);
  }
  if (party && object && !queue) {
    return objectMatchesPartyRole(object.object, party.role);
  }
  return false;
}

/** Party first, then scope — stable chip order. */
export function normalizeTokenStack(
  tokens: readonly SearchToken[],
): SearchToken[] {
  const party = tokens.find((t) => t.type === "party");
  const rest = tokens.filter((t) => t.type !== "party");
  return party ? [party, ...rest] : [...tokens];
}

/**
 * Lock next token onto the stack — plan/35 #5.
 * Compatible → append/replace same type; incompatible → switch to just `next`.
 */
export function applyLockToken(
  stack: readonly SearchToken[],
  next: SearchToken,
): SearchToken[] {
  if (stack.length === 0) return [next];
  const withoutSameType = stack.filter((t) => t.type !== next.type);
  const candidate = normalizeTokenStack([...withoutSameType, next]);
  if (isCompatibleTokenStack(candidate)) return candidate;
  return [next];
}

export function partyQueueListPath(
  companySlug: string,
  party: { id: string; name: string },
  queue: QueueScopeKind,
): string {
  const params = new URLSearchParams();
  params.set("party", party.id);
  params.set("partyName", party.name);
  return `${queueListPath(companySlug, queue)}?${params.toString()}`;
}

/** Open-list for a single token or stacked party+scope — plan/35. */
export function openListItemForStack(
  companySlug: string,
  tokens: readonly SearchToken[],
): {
  id: string;
  label: string;
  hint: string;
  path: string;
} {
  if (tokens.length === 0) {
    return { id: "open-list-empty", label: "Open", hint: "", path: `/c/${companySlug}` };
  }
  if (tokens.length === 1) return openListItemForToken(companySlug, tokens[0]!);

  const party = tokens.find((t) => t.type === "party");
  const queue = tokens.find((t) => t.type === "queue");
  const object = tokens.find((t) => t.type === "object");
  if (party && queue) {
    const meta = QUEUE_META[queue.queue];
    return {
      id: `open-list-party-queue-${party.id}-${queue.queue}`,
      label: meta.listLabel,
      hint: party.name,
      path: partyQueueListPath(companySlug, party, queue.queue),
    };
  }
  if (party && object) {
    const meta = OBJECT_META[object.object];
    const path =
      party.role === "supplier"
        ? partyPurchasingOrdersListPath(companySlug, party)
        : partySalesOrdersListPath(companySlug, party);
    return {
      id: `open-list-party-object-${party.id}-${object.object}`,
      label: meta.listLabel,
      hint: party.name,
      path,
    };
  }
  return openListItemForToken(companySlug, tokens[0]!);
}
